// The counterparty drawer: the compliance position, editable, plus the
// treaties this party actually participates on.
//
// The linked-treaties panel fetches on open rather than coming down with the
// list: it is one request for the record you are looking at, against
// fourteen joins for a list you are only scanning.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import {
  BASE_URL, C, Pill, Panel, Row, MiniTable, money0, date, humanise,
} from './kit';
import {
  useAction, Notice, ActionButton, ActionRow, Input, FieldRow,
  EditPanel, StatusAction, RemoveAction,
} from './actions';

const today = () => new Date().toISOString().slice(0, 10);

function LinkedTreaties({ row: r }) {
  const history = useHistory();
  const [state, setState] = useState({ loading: true, rows: [], error: '' });

  useEffect(() => {
    let dead = false;
    setState({ loading: true, rows: [], error: '' });
    axios.get(`${BASE_URL}/reinsurance/treaties`, {
      params: { limit: 100 }, withCredentials: true,
    })
      .then((res) => {
        if (dead) return;
        const all = res.data?.data || [];
        // Match on the share panel, not on the lead reinsurer alone: a
        // following market is still a participant, and showing only the ones
        // we lead would understate the exposure this drawer exists to show.
        const mine = all.filter(t => (t.shares || []).some(s => s.name === r.name)
          || t.leadReinsurer?.name === r.name
          || t.cedant?.name === r.name);
        setState({ loading: false, rows: mine, error: '' });
      })
      .catch((e) => {
        if (!dead) {
          setState({
            loading: false,
            rows: [],
            error: e?.response?.data?.message || 'Could not load linked treaties.',
          });
        }
      });
    return () => { dead = true; };
  }, [r.name]);

  if (state.loading) return <div style={{ fontSize: 12.5, color: C.muted }}>Loading…</div>;
  if (state.error) return <div style={{ fontSize: 12.5, color: C.red }}>{state.error}</div>;

  return (
    <>
      <MiniTable
        head={['Programme', 'Signed', 'Status']}
        rows={state.rows.map(t => [
          <button
            key={t._id}
            type='button'
            onClick={() => history.push(`/reinsurance/treaties?selected=${t._id}`)}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: C.burgundy, fontWeight: 600, fontSize: 12, fontFamily: 'inherit',
              textAlign: 'left',
            }}
          >
            {t.treatyNumber}
          </button>,
          `${(t.shares || []).find(s => s.name === r.name)?.signedLine ?? 0}%`,
          humanise(t.status),
        ])}
        empty='This party does not participate on any programme yet.'
      />
      {state.rows.length > 0 && (
        <div style={{ fontSize: 11.5, color: C.faint, marginTop: 9, lineHeight: 1.5 }}>
          Click a reference to open that programme.
        </div>
      )}
    </>
  );
}

export default function CounterpartyDrawer({ row: r, tab, reload }) {
  const act = useAction(reload);
  const [rating, setRating] = useState({ agency: 'AM Best', grade: '', ratedAt: today() });

  // ─── Compliance ───
  if (tab === 'Compliance') {
    return (
      <>
        <Notice error={act.error} note={act.note} />
        <Panel title='Compliance position'>
          <div style={{ display: 'grid', gap: 10 }}>
            {/* RECORDING A CHECK, NOT SETTING A FIELD.
                These two controls used to PATCH `kyc` / `sanctions` on the
                counterparty. Both are deliberately absent from CP_WRITABLE
                (see reinsuranceCounterpartyController.js) precisely so a
                position cannot be flipped with no evidence behind it, so
                pickWritable stripped them, the PATCH returned 200 with the
                record unchanged, and the drawer redrew the old value. That is
                the whole of QA's "saves the change but shows old data" — the
                enum values were fixed on 2026-09-22, the refused write was
                not.

                POST /compliance-checks is the endpoint that already does this
                properly: it writes the check as evidence AND moves the
                registry position from the result (pass → clear, fail →
                failed/flagged, review → in progress). So the control offers
                the RESULT of a check, and the position follows from it. */}
            <Row label='KYC position' value={<Pill status={r.kyc?.status || 'not_started'} size='sm' />} />
            <Input
              label='Record a KYC check'
              value=''
              onChange={(v) => v && act.run({
                method: 'post',
                url: '/reinsurance/compliance-checks',
                body: {
                  type: 'kyc', result: v,
                  counterparty: r._id, counterpartyName: r.name,
                  performedAt: new Date().toISOString(),
                },
                success: `KYC check recorded — position follows the result.`,
              })}
              options={[
                // A select shows its first option when `value` does not match
                // any of them, so without this the control would read "Pass"
                // before anyone had chosen anything.
                { value: '', label: 'Choose the result…' },
                { value: 'pass', label: 'Pass — KYC clear' },
                { value: 'review', label: 'Review — in progress' },
                { value: 'fail', label: 'Fail — KYC failed' },
              ]}
            />
            <Row label='Sanctions position' value={<Pill status={r.sanctions?.status || 'not_screened'} size='sm' />} />
            <Input
              label='Record a sanctions screening'
              value=''
              onChange={(v) => v && act.run({
                method: 'post',
                url: '/reinsurance/compliance-checks',
                body: {
                  type: 'sanctions', result: v,
                  counterparty: r._id, counterpartyName: r.name,
                  performedAt: new Date().toISOString(),
                },
                success: `Sanctions screening recorded — position follows the result.`,
              })}
              options={[
                { value: '', label: 'Choose the result…' },
                { value: 'pass', label: 'Pass — clear' },
                { value: 'review', label: 'Review — not concluded' },
                { value: 'fail', label: 'Fail — flagged' },
              ]}
            />
            <Input
              label='Capacity status'
              value={r.capacity?.status || 'open'}
              onChange={(v) => act.run({
                method: 'patch',
                url: `/reinsurance/counterparties/${r._id}`,
                body: { capacity: { ...(r.capacity || {}), status: v } },
                success: `Capacity set to ${humanise(v)}.`,
              })}
              options={[
                { value: 'open', label: 'Open' },
                { value: 'limited', label: 'Limited' },
                { value: 'closed', label: 'Closed' },
                { value: 'under_review', label: 'Under review' },
              ]}
            />
            <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5 }}>
              Each of these stamps its own date when you change it. Capacity is a
              separate question from compliance — a party can be entirely clear and
              still closed because their line is full.
            </div>
          </div>
        </Panel>
      </>
    );
  }

  // ─── Ratings ───
  if (tab === 'Ratings') {
    return (
      <>
        <Notice error={act.error} note={act.note} />
        <Panel title='Credit ratings'>
          <MiniTable
            head={['Agency', 'Grade', 'Rated']}
            rows={(r.ratings || []).map(x => [x.agency, x.grade, date(x.ratedAt)])}
            empty='No rating recorded.'
          />
        </Panel>
        <Panel title='Add a rating'>
          <div style={{ display: 'grid', gap: 9 }}>
            <FieldRow>
              <Input
                label='Agency'
                value={rating.agency}
                onChange={(v) => setRating(p => ({ ...p, agency: v }))}
                options={[
                  { value: 'AM Best', label: 'AM Best' },
                  { value: 'S&P', label: 'S&P' },
                  { value: 'Fitch', label: 'Fitch' },
                  { value: "Moody's", label: "Moody's" },
                ]}
              />
              <Input
                label='Grade'
                value={rating.grade}
                onChange={(v) => setRating(p => ({ ...p, grade: v }))}
                placeholder='AA-'
              />
            </FieldRow>
            <Input
              label='Rated on'
              type='date'
              value={rating.ratedAt}
              onChange={(v) => setRating(p => ({ ...p, ratedAt: v }))}
            />
            <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5 }}>
              Ratings are appended, never overwritten — the date a grade was given
              is what makes it worth anything.
            </div>
            <ActionRow>
              <ActionButton
                busy={act.saving}
                disabled={!rating.grade.trim()}
                onClick={async () => {
                  const ok = await act.run({
                    url: `/reinsurance/counterparties/${r._id}/ratings`,
                    body: { agency: rating.agency, grade: rating.grade.trim(), ratedAt: rating.ratedAt },
                    success: 'Rating recorded.',
                  });
                  if (ok) setRating({ agency: 'AM Best', grade: '', ratedAt: today() });
                }}
              >
                Add rating
              </ActionButton>
            </ActionRow>
          </div>
        </Panel>
      </>
    );
  }

  // ─── Treaties ───
  if (tab === 'Treaties') {
    return (
      <Panel title='Programmes this party is on'>
        <LinkedTreaties row={r} />
      </Panel>
    );
  }

  // ─── Overview ───
  return (
    <>
      <Notice error={act.error} note={act.note} />

      <Panel title='Key information'>
        <Row label='Legal name' value={r.name} />
        <Row label='Type' value={humanise(r.type)} />
        <Row label='Domicile' value={r.country} />
        <Row label='Relationship since' value={date(r.relationshipSince)} />
        <Row label='Owner' value={r.ownerName} />
        <Row label='Status' value={<Pill status={r.status} size='sm' />} />
      </Panel>

      <Panel title='Compliance'>
        <Row label='KYC' value={<Pill status={r.kyc?.status || 'not_started'} size='sm' />} />
        {/* `lastCheckedAt` is not a field on the kyc subdoc — it is
            `completedAt` (see reinsuranceCounterpartyModel.js), so this row
            rendered an em dash on every counterparty however many checks
            had been done. */}
        <Row label='Last checked' value={date(r.kyc?.completedAt)} />
        <Row label='Sanctions' value={<Pill status={r.sanctions?.status || 'not_screened'} size='sm' />} />
        <Row label='Last screened' value={date(r.sanctions?.lastScreenedAt)} />
        <Row label='Capacity' value={<Pill status={r.capacity?.status || 'open'} size='sm' />} />
        <Row label='Typical capacity' value={money0(r.capacity?.typicalCapacity, r.capacity?.currency)} />
      </Panel>

      <Panel title='Programmes this party is on'>
        <LinkedTreaties row={r} />
      </Panel>

      {/* ── The record itself ──
          Correcting, moving and withdrawing a record, in one place on the
          Overview tab. Before this a screen could create a record and then
          never touch it again. Removal is a void or a cancel, never a
          delete — see reinsuranceShared.js. */}
      <Panel title='Record'>
        <div style={{ display: 'grid', gap: 10 }}>
          <EditPanel
            record={r}
            endpoint='/reinsurance/counterparties'
            onSaved={reload}
            fields={[
            { key: 'name', label: 'Name' },
            { key: 'legalName', label: 'Legal name' },
            { key: 'country', label: 'Domicile' },
            { key: 'registrationNumber', label: 'Registration no.' },
            { key: 'licenceNumber', label: 'Licence no.' },
            ]}
          />
          <StatusAction
            record={r}
            endpoint='/reinsurance/counterparties'
            onSaved={reload}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'suspended', label: 'Suspended' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />
          <RemoveAction record={r} endpoint='/reinsurance/counterparties' noun='counterparty' onRemoved={reload} />
        </div>
      </Panel>

      <Panel title='Actions'>
        <ActionRow>
          <ActionButton
            busy={act.saving}
            onClick={() => act.run({
              url: '/reinsurance/sanctions/screen',
              body: { name: r.name, counterparty: r._id },
              success: 'Screening requested.',
            })}
          >
            Run screening
          </ActionButton>
          <ActionButton
            tone='quiet'
            busy={act.saving}
            onClick={() => act.run({
              method: 'patch',
              url: `/reinsurance/counterparties/${r._id}`,
              body: { lastReviewAt: new Date().toISOString() },
              success: 'Review date updated to today.',
            })}
          >
            Mark reviewed
          </ActionButton>
        </ActionRow>
      </Panel>
    </>
  );
}
